import React, { useState, useEffect, useRef } from "react";
import { useAgent } from "../hooks/useAgent";
import { AGENT_PERSONALITIES } from "../agentPersonalities";
import { MethodSheet } from "./MethodSheet";

const Z_FALLBACK = {
  primary: "#55624d", primaryFixed: "#d9e7cd", primaryFixedDim: "#bdcbb2",
  onPrimary: "#ffffff", surface: "#f8faf3", surfaceContainer: "#ecefe8",
  surfaceContainerLow: "#f2f4ed", surfaceContainerLowest: "#ffffff",
  onSurface: "#191c18", onSurfaceVariant: "#444841",
  secondaryContainer: "#fed7d2", onSecondaryContainer: "#795b58",
  outline: "#757870", outlineVariant: "#c5c8be",
  error: "#ba1a1a", inverseSurface: "#2e312d", inverseOnSurface: "#eff2ea",
};

// Z wird von App.js global gesetzt — Fallback für Safety
let Z = Z_FALLBACK;
export function setThemeTokens(tokens) { Z = tokens; }

// ─────────────────────────────────────────────
// TYPING DOTS
// ─────────────────────────────────────────────
function TypingIndicator({ color }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "10px 14px" }}>
      {[0,1,2].map(i => (
        <div key={i} style={{ width: 7, height: 7, borderRadius: "50%", background: color || Z.outline, animation: "typingBounce 1.2s ease-in-out infinite", animationDelay: `${i*0.15}s`, opacity: 0.7 }} />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
// SUPPLEMENT KARTE — strukturierte Darstellung
// ─────────────────────────────────────────────
function SupplementCard({ content, onShowMethod }) {
  let data = null;
  try {
    data = JSON.parse(content);
  } catch {
    // Fallback: roher Text
    return (
      <div style={{ background: Z.surfaceContainerLowest, borderRadius: "4px 18px 18px 18px", padding: "12px 16px", fontSize: 14, color: Z.onSurface, border: `1px solid ${Z.outlineVariant}`, whiteSpace: "pre-wrap" }}>
        {content}
      </div>
    );
  }

  const naehrstoffe = data.naehrstoffe || [];
  const anyOver     = naehrstoffe.some(n => n.status === "ueber");
  const statusStyle = {
    ok:     { icon: "✓", color: "#16a34a", text: "im Rahmen" },
    ueber:  { icon: "⚠", color: "#dc2626", text: "über BfR-Empfehlung" },
    unklar: { icon: "?", color: Z.outline, text: "unklar" },
  };

  return (
    <div style={{ background: Z.surfaceContainerLowest, border: `1px solid ${anyOver ? "#fca5a5" : Z.outlineVariant}`, borderRadius: "4px 18px 18px 18px", overflow: "hidden", fontFamily: "'Plus Jakarta Sans', sans-serif", maxWidth: "100%" }}>

      {/* Header: Name */}
      <div style={{ padding: "14px 18px 10px", borderBottom: `1px solid ${Z.outlineVariant}` }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: Z.primary, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2 }}>Etikett abgelesen</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: Z.onSurface, lineHeight: 1.3 }}>{data.name}</div>
        {data.form && <div style={{ fontSize: 11, color: Z.onSurfaceVariant, marginTop: 2 }}>{data.form}</div>}
      </div>

      {/* Nährstoffe vs. BfR-Höchstmengen */}
      {naehrstoffe.length > 0 && (
        <div style={{ padding: "10px 18px", display: "flex", flexDirection: "column", gap: 6 }}>
          {naehrstoffe.map((n, i) => {
            const st = statusStyle[n.status] || statusStyle.unklar;
            return (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: Z.onSurface }}>
                <span style={{ color: st.color, flexShrink: 0, marginTop: 1 }}>{st.icon}</span>
                <span style={{ flex: 1 }}>{n.stoff}: {n.menge}</span>
                <span style={{ fontSize: 11, color: st.color, flexShrink: 0 }}>{st.text}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Hinweise */}
      {(data.hinweise || []).length > 0 && (
        <div style={{ padding: "0 18px 10px", display: "flex", flexDirection: "column", gap: 4 }}>
          {data.hinweise.map((h, i) => (
            <div key={i} style={{ fontSize: 12, color: Z.onSurfaceVariant, lineHeight: 1.5 }}>• {h}</div>
          ))}
        </div>
      )}

      {/* Ältere Analysen (vor dem Audit) hatten andere Felder */}
      {naehrstoffe.length === 0 && !data.hinweise && (
        <div style={{ padding: "10px 18px", fontSize: 12, color: Z.onSurfaceVariant }}>Ältere Analyse. Lade das Etikett erneut hoch für die aktuelle Auswertung.</div>
      )}

      <div style={{ padding: "8px 18px 12px", fontSize: 11, color: Z.outline, lineHeight: 1.5 }}>
        Vergleich mit den Höchstmengen-Empfehlungen des BfR für Nahrungsergänzungsmittel. Keine medizinische Beratung.
        {onShowMethod && (
          <> <button onClick={onShowMethod} style={{ border: "none", background: "none", padding: 0, color: Z.primary, fontSize: 11, textDecoration: "underline", cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>So prüfen wir</button></>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// PRODUKT-KARTE — Affiliate Empfehlung
// ─────────────────────────────────────────────
function ProductCard({ product, onClose, onShowMethod }) {
  if (!product) return null;

  const handleBuy = () => {
    window.open(product.affiliate_url, "_blank", "noopener,noreferrer");
  };

  return (
    <div style={{ margin: "8px 0", background: Z.surfaceContainerLowest, border: `1px solid ${Z.primaryFixedDim}`, borderRadius: 16, overflow: "hidden", fontFamily: "'Plus Jakarta Sans', sans-serif", animation: "fadeIn 0.3s ease" }}>
      <div style={{ background: Z.primaryFixed, padding: "10px 14px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: Z.primary, textTransform: "uppercase", letterSpacing: "0.06em" }}>Anzeige</span>
          <span style={{ fontSize: 11, color: Z.onSurfaceVariant }}>· Wir erhalten eine Provision</span>
          {onShowMethod && <button onClick={onShowMethod} style={{ border: "none", background: "none", padding: 0, color: Z.primary, fontSize: 11, textDecoration: "underline", cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Mehr dazu</button>}
        </div>
        {onClose && <button onClick={onClose} style={{ border: "none", background: "none", fontSize: 14, cursor: "pointer", color: Z.outline }}>✕</button>}
      </div>
      <div style={{ padding: "12px 14px", display: "flex", gap: 12, alignItems: "flex-start" }}>
        <div style={{ width: 56, height: 56, borderRadius: 10, background: Z.surfaceContainer, flexShrink: 0, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {product.image_url
            ? <img src={product.image_url} alt={product.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <span style={{ fontSize: 24 }}>💊</span>}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, color: Z.primary, fontWeight: 700, marginBottom: 2 }}>{product.brand}</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: Z.onSurface, lineHeight: 1.3, marginBottom: 4 }}>{product.name}</div>
          {product.key_benefit && <div style={{ fontSize: 12, color: Z.onSurfaceVariant, lineHeight: 1.4 }}>✓ {product.key_benefit}</div>}
        </div>
      </div>
      <div style={{ padding: "0 14px 14px", display: "flex", alignItems: "center", gap: 10 }}>
        {product.price_eur && <div style={{ fontSize: 15, fontWeight: 700, color: Z.onSurface }}>€{Number(product.price_eur).toFixed(2)}</div>}
        <button onClick={handleBuy} style={{ marginLeft: "auto", padding: "9px 18px", borderRadius: 20, border: "none", background: Z.primary, color: Z.onPrimary, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif", boxShadow: "0 4px 12px rgba(85,98,77,0.25)" }}>
          Zum Shop →
        </button>
      </div>
    </div>
  );
}
function MessageBubble({ msg, personality, onShowMethod }) {
  const isUser = msg.role === "user";
  const p = AGENT_PERSONALITIES[msg.personality || personality];

  const isProactive    = msg.content?.startsWith("[PROACTIVE]");
  const isWeeklyReview = msg.content?.startsWith("[WEEKLY_REVIEW]");
  const isSupplementAnalysis = msg.isSupplementAnalysis;

  const displayContent = msg.content
    ?.replace("[PROACTIVE] ", "")
    ?.replace("[WEEKLY_REVIEW] ", "")
    || "";

  const time = msg.timestamp
    ? new Date(msg.timestamp).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })
    : "";

  if (isUser) {
    return (
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10, paddingLeft: 56 }}>
        <div style={{ maxWidth: "100%" }}>
          {msg.imagePreview && (
            <div style={{ marginBottom: 6, textAlign: "right" }}>
              <img src={msg.imagePreview} alt="Etikett" style={{ maxHeight: 120, maxWidth: 200, borderRadius: 12, border: `2px solid ${Z.primary}`, objectFit: "cover" }} />
            </div>
          )}
          {displayContent && (
            <div style={{ background: Z.primary, color: Z.onPrimary, borderRadius: "18px 18px 4px 18px", padding: "11px 16px", fontSize: 15, lineHeight: 1.6, fontFamily: "'Plus Jakarta Sans', sans-serif", wordBreak: "break-word" }}>
              {displayContent}
            </div>
          )}
          <div style={{ fontSize: 10, color: Z.outline, textAlign: "right", marginTop: 3, paddingRight: 4, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{time}</div>
        </div>
      </div>
    );
  }

  if (isWeeklyReview) {
    return (
      <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "flex-start", paddingRight: 20 }}>
        <div style={{ width: 32, height: 32, borderRadius: "50%", flexShrink: 0, background: p?.color || Z.inverseSurface, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, marginTop: 2 }}>
          {p?.emoji || "🤖"}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10, color: Z.primary, fontWeight: 700, marginBottom: 4, fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: "0.06em", textTransform: "uppercase" }}>✦ Wöchentliches Review</div>
          <div style={{ background: Z.primaryFixed, borderRadius: "4px 18px 18px 18px", padding: "12px 16px", fontSize: 14, lineHeight: 1.75, fontFamily: "'Plus Jakarta Sans', sans-serif", color: Z.onSurface, border: `1px solid ${Z.primaryFixedDim}`, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {displayContent}
          </div>
          <div style={{ fontSize: 10, color: Z.outline, marginTop: 3, paddingLeft: 4, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{time}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "flex-start", paddingRight: 56 }}>
      <div style={{ width: 32, height: 32, borderRadius: "50%", flexShrink: 0, alignSelf: "flex-start", background: msg.isError ? "#fee2e2" : (p?.color || Z.inverseSurface), display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, marginTop: 2 }}>
        {msg.isError ? "⚠️" : (p?.emoji || "🤖")}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {isProactive && (
          <div style={{ fontSize: 10, color: Z.primary, fontWeight: 700, marginBottom: 4, fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: "0.06em", textTransform: "uppercase" }}>✦ Proaktiv</div>
        )}
        {isSupplementAnalysis ? (
          <>
            <SupplementCard content={displayContent} onShowMethod={onShowMethod} />
          </>
        ) : (
          <div style={{ background: msg.isError ? "#fee2e2" : isProactive ? Z.primaryFixed : Z.surfaceContainerLowest, color: msg.isError ? "#991b1b" : Z.onSurface, borderRadius: "4px 18px 18px 18px", padding: "11px 16px", fontSize: 14, lineHeight: 1.7, fontFamily: "'Plus Jakarta Sans', sans-serif", border: `1px solid ${msg.isError ? "#fca5a5" : isProactive ? Z.primaryFixedDim : Z.outlineVariant}`, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {displayContent}
          </div>
        )}
        <div style={{ fontSize: 10, color: Z.outline, marginTop: 3, paddingLeft: 4, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{time}</div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// PRODUKT-EMPFEHLUNGS-KARTE
// ─────────────────────────────────────────────
export function PersonalitySelector({ current, onChange, onClose }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(25,28,24,0.6)", zIndex: 1000, display: "flex", alignItems: "flex-end" }} onClick={onClose}>
      <div style={{ background: Z.surface, width: "100%", maxHeight: "92vh", borderRadius: "24px 24px 0 0", display: "flex", flexDirection: "column", overflow: "hidden" }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px 8px", flexShrink: 0 }}>
          <div style={{ width: 32 }} />
          <div style={{ width: 36, height: 4, borderRadius: 2, background: Z.outlineVariant }} />
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: "50%", border: "none", background: Z.surfaceContainerHigh, color: Z.onSurfaceVariant, fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
        </div>
        <div style={{ padding: "0 20px 12px", flexShrink: 0 }}>
          <h3 style={{ fontFamily: "'Manrope', sans-serif", fontSize: 18, fontWeight: 500, color: Z.onSurface, margin: 0 }}>Wähle deinen Begleiter</h3>
        </div>
        <div style={{ overflowY: "auto", padding: "0 20px 40px", display: "flex", flexDirection: "column", gap: 10, flex: 1, WebkitOverflowScrolling: "touch" }}>
          {Object.values(AGENT_PERSONALITIES).map(p => (
            <button key={p.id} onClick={() => { onChange(p.id); onClose(); }} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", borderRadius: 16, border: `${current === p.id ? "2px" : "1px"} solid ${current === p.id ? p.color : Z.outlineVariant}`, background: current === p.id ? `${p.color}18` : Z.surfaceContainerLowest, cursor: "pointer", textAlign: "left", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              <div style={{ width: 44, height: 44, borderRadius: 14, background: p.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>{p.emoji}</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 15, fontWeight: 600, color: Z.onSurface }}>{p.name}</span>
                  {current === p.id && <span style={{ fontSize: 10, background: p.color, color: "#fff", borderRadius: 6, padding: "2px 7px", fontWeight: 700 }}>AKTIV</span>}
                </div>
                <div style={{ fontSize: 12, color: Z.primary, fontWeight: 600, marginTop: 1 }}>{p.tagline}</div>
                <div style={{ fontSize: 12, color: Z.onSurfaceVariant, marginTop: 2, lineHeight: 1.4 }}>{p.description}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// QUICK REPLIES
// ─────────────────────────────────────────────
const QUICK_REPLIES = {
  coach:     ["Wo stehe ich?", "Push mich.", "Was fehlt heute?", "Wöchentliches Review"],
  friend:    ["Wie geht's dir?", "Was hab ich heute gemacht?", "Ich brauch Motivation.", "Wöchentliches Review"],
  scientist: ["Zeig mir meine Daten.", "Was läuft gut?", "Wöchentliches Review", "Meine Schwachstellen?"],
  monk:      ["Was sollte ich jetzt tun?", "Ich bin müde.", "Wöchentliches Review", "Warum mache ich das?"],
};

// ─────────────────────────────────────────────
// HABIT SUGGESTION CARD
// ─────────────────────────────────────────────
function HabitSuggestionCard({ suggestion, onDismiss }) {
  if (!suggestion) return null;
  return (
    <div style={{ margin: "8px 16px", padding: "12px 16px", background: Z.secondaryContainer, borderRadius: 14, border: `1px solid ${Z.outlineVariant}`, display: "flex", gap: 10, alignItems: "flex-start" }}>
      <span style={{ fontSize: 18, flexShrink: 0 }}>💡</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: Z.primary, marginBottom: 4, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Empfehlung</div>
        <div style={{ fontSize: 13, color: Z.onSecondaryContainer, fontFamily: "'Plus Jakarta Sans', sans-serif", lineHeight: 1.5 }}>{suggestion.message}</div>
      </div>
      <button onClick={onDismiss} style={{ border: "none", background: "none", fontSize: 16, cursor: "pointer", color: Z.outline, flexShrink: 0 }}>✕</button>
    </div>
  );
}

// Für diese Kategorien zeigen wir nie ein Partnerprodukt (Risiko-Stoffe bzw. unklar)
const NO_PRODUCT_CATEGORIES = ["iron", "ashwagandha", "other"];

// ─────────────────────────────────────────────
// AGENT CHAT
// ─────────────────────────────────────────────
export function AgentChat({ userId, habitData, onShowPersonalitySelector }) {
  const {
    personality, messages, isTyping, loading,
    habitSuggestion, changePersonality,
    sendMessage, loadProactiveGreeting,
    generateWeeklyReview, clearChat,
  } = useAgent(userId, habitData);

  const [input, setInput]                 = useState("");
  const [showOptions, setOptions]         = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [localSuggestion, setLocalSuggestion] = useState(null);
  const [productRec, setProductRec]       = useState(null);
  const [showProduct, setShowProduct]     = useState(false);
  const [showMethod, setShowMethod]       = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef       = useRef(null);
  const fileInputRef   = useRef(null);
  const hasSentGreeting = useRef(false);

  const p = AGENT_PERSONALITIES[personality];

  // Z für Sub-Komponenten sync halten
  useEffect(() => {
    const style = document.documentElement.style;
    Z = {
      primary:                style.getPropertyValue("--z-primary").trim() || Z_FALLBACK.primary,
      primaryFixed:           style.getPropertyValue("--z-primary-fixed").trim() || Z_FALLBACK.primaryFixed,
      primaryFixedDim:        style.getPropertyValue("--z-primary-fixed-dim").trim() || Z_FALLBACK.primaryFixedDim,
      onPrimary:              style.getPropertyValue("--z-on-primary").trim() || Z_FALLBACK.onPrimary,
      surface:                style.getPropertyValue("--z-surface").trim() || Z_FALLBACK.surface,
      surfaceContainerLowest: style.getPropertyValue("--z-surface-container-lowest").trim() || Z_FALLBACK.surfaceContainerLowest,
      surfaceContainerHigh:   style.getPropertyValue("--z-surface-container-high").trim() || Z_FALLBACK.surfaceContainerHigh,
      onSurface:              style.getPropertyValue("--z-on-surface").trim() || Z_FALLBACK.onSurface,
      onSurfaceVariant:       style.getPropertyValue("--z-on-surface-variant").trim() || Z_FALLBACK.onSurfaceVariant,
      secondaryContainer:     style.getPropertyValue("--z-secondary-container").trim() || Z_FALLBACK.secondaryContainer,
      onSecondaryContainer:   style.getPropertyValue("--z-on-secondary-container").trim() || Z_FALLBACK.onSecondaryContainer,
      outline:                style.getPropertyValue("--z-outline").trim() || Z_FALLBACK.outline,
      outlineVariant:         style.getPropertyValue("--z-outline-variant").trim() || Z_FALLBACK.outlineVariant,
      error:                  style.getPropertyValue("--z-error").trim() || Z_FALLBACK.error,
      inverseSurface:         style.getPropertyValue("--z-inverse-surface").trim() || Z_FALLBACK.inverseSurface,
      inverseOnSurface:       style.getPropertyValue("--z-inverse-on-surface").trim() || Z_FALLBACK.inverseOnSurface,
    };
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  useEffect(() => {
    if (!loading && messages.length === 0 && !hasSentGreeting.current) {
      hasSentGreeting.current = true;
      const t = setTimeout(() => loadProactiveGreeting(), 100);
      return () => clearTimeout(t);
    }
  }, [loading, messages.length]);

  useEffect(() => {
    if (habitSuggestion) setLocalSuggestion(habitSuggestion);
  }, [habitSuggestion]);

  // Produkt-Empfehlung nach Supplement-Analyse laden
  useEffect(() => {
    const lastMsg = messages[messages.length - 1];
    if (!lastMsg?.isSupplementAnalysis) return;
    try {
      const data = JSON.parse(lastMsg.content);
      const overLimit = (data.naehrstoffe || []).some(n => n.status === "ueber");
      setProductRec(null);
      setShowProduct(false);
      if (data.category && !overLimit && !NO_PRODUCT_CATEGORIES.includes(data.category)) fetchProductRecommendation(data.category);
    } catch {}
  }, [messages]);

  const fetchProductRecommendation = async (category) => {
    try {
      const { supabase } = await import("../supabaseClient");
      // Regel: Auswahl und Reihenfolge hängen nie von der Provision ab
      // (siehe docs/GESUNDHEITS-AUDIT.md). Deshalb kein Sortieren nach score oder commission_rate.
      const { data } = await supabase
        .from("products")
        .select("id, name, brand, category, key_benefit, price_eur, image_url, affiliate_url")
        .eq("category", category)
        .eq("active", true)
        .not("affiliate_url", "ilike", "%PLACEHOLDER%")
        .order("name")
        .limit(1);
      if (data && data.length > 0) setProductRec(data[0]);
    } catch {}
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setSelectedImage({
        preview:   ev.target.result,
        base64:    ev.target.result.split(",")[1],
        mediaType: file.type || "image/jpeg",
      });
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleSend = (text) => {
    const msg = text || input;
    if (!msg.trim() && !selectedImage) return;

    if (msg === "Wöchentliches Review") {
      generateWeeklyReview();
      setInput("");
      return;
    }

    if (selectedImage) {
      const userText = msg.trim() || "Analysiere dieses Supplement-Etikett bitte.";
      sendMessage(userText, selectedImage);
      setSelectedImage(null);
    } else {
      sendMessage(msg);
    }
    setInput("");
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const quickReplies = QUICK_REPLIES[personality] || [];

  if (loading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 20px", gap: 12 }}>
        <div style={{ fontSize: 32 }}>{p?.emoji || "🤖"}</div>
        <span style={{ fontSize: 14, color: Z.outline, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Dein Begleiter wird vorbereitet…</span>
      </div>
    );
  }

  return (
    <>
      {showMethod && <MethodSheet Z={Z} onClose={() => setShowMethod(false)} />}
      <style>{`
        @keyframes typingBounce { 0%,60%,100%{transform:translateY(0)} 30%{transform:translateY(-6px)} }
        @keyframes fadeIn { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
      `}</style>

      <div style={{ display: "flex", flexDirection: "column", height: "100%", width: "100%", overflow: "hidden", background: Z.surface }}>

        {/* ── HEADER ── */}
        <div style={{ padding: "12px 16px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${Z.outlineVariant}`, background: Z.surface }}>
          <button onClick={() => onShowPersonalitySelector?.(personality, changePersonality)} style={{ display: "flex", alignItems: "center", gap: 10, border: "none", background: "none", cursor: "pointer", padding: 0, minWidth: 0, flex: 1, overflow: "hidden" }}>
            <div style={{ width: 36, height: 36, borderRadius: "50%", background: p?.color || Z.inverseSurface, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, flexShrink: 0 }}>
              {p?.emoji || "🤖"}
            </div>
            <div style={{ textAlign: "left", minWidth: 0, overflow: "hidden" }}>
              <div style={{ fontFamily: "'Manrope', sans-serif", fontSize: 14, fontWeight: 600, color: Z.onSurface, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p?.name || "Agent"}</div>
              <div style={{ fontSize: 11, color: Z.primary, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 4 }}>
                <span>{p?.tagline}</span>
                <span style={{ opacity: 0.6 }}>· wechseln ›</span>
              </div>
            </div>
          </button>

          <div style={{ position: "relative", flexShrink: 0, marginLeft: 8 }}>
            <button onClick={() => setOptions(!showOptions)} style={{ border: "none", background: "none", fontSize: 20, cursor: "pointer", color: Z.outline, padding: "4px 8px", display: "flex", alignItems: "center" }}>···</button>
            {showOptions && (
              <div style={{ position: "fixed", right: 16, top: 80, background: Z.surfaceContainerLowest, border: `1px solid ${Z.outlineVariant}`, borderRadius: 12, padding: "6px 0", boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 200, minWidth: 180 }}>
                {[
                  { icon: "📊", label: "Wöchentliches Review", action: () => { generateWeeklyReview(); setOptions(false); } },
                  { icon: "🎭", label: "Begleiter wechseln",   action: () => { onShowPersonalitySelector?.(personality, changePersonality); setOptions(false); } },
                  { icon: "🔄", label: "Verlauf löschen",      action: () => { clearChat(); setOptions(false); } },
                ].map(({ icon, label, action }) => (
                  <button key={label} onClick={action} style={{ width: "100%", padding: "10px 16px", border: "none", background: "none", display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: Z.onSurface, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif", textAlign: "left" }}>
                    <span>{icon}</span> {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── MESSAGES ── */}
        <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: "16px 16px 8px", display: "flex", flexDirection: "column", WebkitOverflowScrolling: "touch" }}>
          {messages.length === 0 && !isTyping && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, gap: 12, textAlign: "center", padding: "40px 20px" }}>
              <div style={{ width: 60, height: 60, borderRadius: "50%", background: p?.color || Z.inverseSurface, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26 }}>{p?.emoji || "🤖"}</div>
              <h3 style={{ fontFamily: "'Manrope', sans-serif", fontSize: 18, fontWeight: 500, color: Z.onSurface, margin: 0 }}>{p?.name} ist bereit</h3>
              <p style={{ fontSize: 13, color: Z.onSurfaceVariant, fontFamily: "'Plus Jakarta Sans', sans-serif", lineHeight: 1.6, maxWidth: 260, margin: 0 }}>{p?.description}</p>
              {/* Kamera-Hinweis */}
              <div style={{ marginTop: 8, padding: "10px 16px", background: Z.primaryFixed, borderRadius: 12, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 18 }}>📷</span>
                <span style={{ fontSize: 12, color: Z.primary, fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Supplement-Etikett fotografieren für KI-Analyse</span>
              </div>
            </div>
          )}

          {messages.map(msg => (
            <div key={msg.id} style={{ animation: "fadeIn 0.25s ease forwards" }}>
              <MessageBubble msg={msg} personality={personality} onShowMethod={() => setShowMethod(true)} />
            </div>
          ))}

          {isTyping && (
            <div style={{ display: "flex", gap: 8, paddingRight: "15%", alignItems: "flex-start", animation: "fadeIn 0.2s ease forwards" }}>
              <div style={{ width: 32, height: 32, borderRadius: "50%", background: p?.color || Z.inverseSurface, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0, marginTop: 2 }}>{p?.emoji || "🤖"}</div>
              <div style={{ background: Z.surfaceContainerLowest, borderRadius: "4px 18px 18px 18px", border: `1px solid ${Z.outlineVariant}` }}>
                <TypingIndicator color={p?.color} />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* ── HABIT SUGGESTION ── */}
        <HabitSuggestionCard suggestion={localSuggestion} onDismiss={() => setLocalSuggestion(null)} />

        {/* ── PRODUKT EMPFEHLUNG ── */}
        {/* Werbung nur auf ausdrücklichen Wunsch und klar gekennzeichnet */}
        {productRec && !showProduct && (
          <div style={{ padding: "0 16px 8px" }}>
            <button onClick={() => setShowProduct(true)} style={{ border: `1px solid ${Z.outlineVariant}`, background: Z.surfaceContainerLowest, color: Z.onSurfaceVariant, borderRadius: 14, padding: "6px 12px", fontSize: 12, cursor: "pointer", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Anzeige: Produkt aus unserem Partnerprogramm ansehen
            </button>
          </div>
        )}
        {productRec && showProduct && (
          <div style={{ padding: "0 16px 8px" }}>
            <ProductCard product={productRec} onClose={() => { setProductRec(null); setShowProduct(false); }} onShowMethod={() => setShowMethod(true)} />
          </div>
        )}

        {/* ── QUICK REPLIES ── */}
        {messages.length <= 2 && !isTyping && (
          <div style={{ padding: "6px 16px 2px", display: "flex", gap: 8, overflowX: "scroll", flexShrink: 0, WebkitOverflowScrolling: "touch", paddingRight: 16 }}>
            {quickReplies.map(reply => (
              <button key={reply} onClick={() => handleSend(reply)} style={{ padding: "7px 14px", borderRadius: 20, border: `1px solid ${Z.outlineVariant}`, background: Z.surfaceContainerLowest, fontSize: 12, color: Z.onSurfaceVariant, cursor: "pointer", whiteSpace: "nowrap", fontFamily: "'Plus Jakarta Sans', sans-serif", flexShrink: 0 }}>
                {reply}
              </button>
            ))}
            <div style={{ width: 8, flexShrink: 0 }} />
          </div>
        )}

        {/* ── INPUT ── */}
        <div style={{ padding: "10px 12px 16px", display: "flex", flexDirection: "column", gap: 8, background: Z.surface, flexShrink: 0, borderTop: `1px solid ${Z.outlineVariant}44` }}>

          {/* Bild-Preview */}
          {selectedImage && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: Z.primaryFixed, borderRadius: 12, border: `1px solid ${Z.primaryFixedDim}` }}>
              <img src={selectedImage.preview} alt="Etikett" style={{ height: 52, width: 52, borderRadius: 8, objectFit: "cover", flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: Z.primary, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>📷 Etikett bereit zur Analyse</div>
                <div style={{ fontSize: 11, color: Z.onSurfaceVariant, fontFamily: "'Plus Jakarta Sans', sans-serif", marginTop: 2 }}>Optional: Frage zum Produkt eingeben</div>
              </div>
              <button onClick={() => setSelectedImage(null)} style={{ width: 24, height: 24, borderRadius: "50%", border: "none", background: Z.error, color: "#fff", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>✕</button>
            </div>
          )}

          <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
            {/* Hidden file input */}
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageSelect} style={{ display: "none" }} />

            {/* Kamera Button */}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isTyping}
              title="Supplement-Etikett fotografieren"
              style={{ width: 44, height: 44, borderRadius: "50%", border: `1px solid ${selectedImage ? Z.primary : Z.outlineVariant}`, background: selectedImage ? Z.primaryFixed : Z.surfaceContainerLowest, color: selectedImage ? Z.primary : Z.outline, fontSize: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.2s" }}
            >
              📷
            </button>

            <textarea
              ref={inputRef}
              value={input}
              onChange={e => { setInput(e.target.value); e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px"; }}
              onKeyDown={handleKeyDown}
              placeholder={selectedImage ? "Frage zum Etikett… (optional)" : (isTyping ? "…" : "Schreib etwas…")}
              disabled={isTyping}
              rows={1}
              style={{ flex: 1, padding: "11px 14px", borderRadius: 20, border: `1px solid ${Z.outlineVariant}`, background: Z.surfaceContainerLowest, fontSize: 16, color: Z.onSurface, fontFamily: "'Plus Jakarta Sans', sans-serif", outline: "none", resize: "none", lineHeight: 1.5, overflow: "hidden", minHeight: 44, WebkitAppearance: "none" }}
              onFocus={e => { e.target.style.borderColor = Z.primary; }}
              onBlur={e => { e.target.style.borderColor = Z.outlineVariant; }}
            />
            <button
              onClick={() => handleSend()}
              disabled={(!input.trim() && !selectedImage) || isTyping}
              style={{ width: 44, height: 44, borderRadius: "50%", border: "none", flexShrink: 0, background: (input.trim() || selectedImage) && !isTyping ? Z.primary : Z.outlineVariant, color: Z.onPrimary, fontSize: 18, cursor: (input.trim() || selectedImage) && !isTyping ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.2s" }}
            >
              ↑
            </button>
          </div>
          <div style={{ fontSize: 10, color: Z.outline, textAlign: "center", fontFamily: "'Plus Jakarta Sans', sans-serif", lineHeight: 1.4 }}>
            KI-Begleiter · keine medizinische Beratung · In einer Krise: TelefonSeelsorge 0800 111 0 111 · Notfall 112
          </div>
        </div>
      </div>
    </>
  );
}